/**
 * The `identify` emit host: one call writes the signed-in user's traits to
 * Datadog RUM, Snowplow, PostHog, Segment, Amplitude and Firebase Remote Config.
 * Lifted out of the analytics model's effects verbatim; the provider it tags
 * on Amplitude is read from the store at emit time.
 */
import { getAppType, segmentClient } from '@/lib/analytics/legacy-sender-context';
import { readStoreAuthProviderForAnalytics } from '@/lib/auth/effectiveAuthProvider';
import {
  DESKTOP_NATIVE_PLATFORM_SOURCE,
  formatRemoteConfigOsVersion,
  formatRemoteConfigRegion,
  setRemoteConfigCustomSignals,
} from '@/lib/firebase/custom-signals';
import { setSnowplowUserContext, setSnowplowUserId } from '@/lib/snowplow';
import {
  mapAuthProviderToProvider,
  type SessionPageV2EnrollmentSource,
} from '@/models/analytics/features/user';
import { isTauri } from '@/tauri/utils';
import {
  type AppType,
  type IntegrationRequest,
  type SeenModals,
  type UserRole,
} from '@/types';
import {
  type DISTRIBUTION_CHANNEL,
  type TAURI_INSTALLER_TYPE,
} from '@/utils/appConfiguration';
import { getAppVersionTraits } from '@/utils/appVersion';
import { getDesktopOsVersion } from '@/utils/desktopOsInfo';
import { getAttributionIdentifyTraits } from '@/utils/utmTracking';
import * as amplitude from '@amplitude/analytics-browser';
import { Identify as AmplitudeIdentify } from '@amplitude/analytics-browser';
import { datadogRum } from '@datadog/browser-rum';
import {
  type EHRProvider,
  type LanguageCode,
  type Region,
  type SubscriptionTier,
} from '@heidi/schema/ml-scribe';
import { type Maybe } from '@heidi/utils';
import type { RoleModel } from '@heidi/workspace/contracts';
import { type ClinicianTypeInput } from '@heidi/workspace/contracts';
import posthog from 'posthog-js';

/** Extract ValidPropertyType from Amplitude's Identify.set() method signature */
type AmplitudePropertyType = Parameters<AmplitudeIdentify['set']>[1];

export interface CommonTraits {
  email: string;
  email_domain?: string;
  App: AppType;
  // Device/build dimensions — optional on identify because they are NOT user
  // identity (they ride per-event via trackEventV2/trackEvent). Kept in the type
  // for any legacy reader; the identify payload no longer sets them.
  app_type?: AppType;
  client_platform?: AppType;
  platform?: 'web' | 'desktop' | 'ios' | 'android';
  installer_type?: TAURI_INSTALLER_TYPE;
  distribution_channel?: DISTRIBUTION_CHANNEL;
  mdm_provider?: string | null;
  cpuArch?: string;
  country?: Maybe<string>;
  region?: Maybe<Region>;
  given_name?: Maybe<string>;
  family_name?: Maybe<string>;
  phone_number?: Maybe<string>;
  has_phone_number?: boolean;
  user_team_id?: Maybe<string>;
  user_team_name?: Maybe<string>;
  user_team_role?: Maybe<UserRole>;
  user_team_plan?: Maybe<string>;
  user_team_mfa_enabled?: Maybe<boolean>;
  user_joined_team_date?: Maybe<string>;
  user_created_date?: Maybe<string>;
  user_tier?: Maybe<SubscriptionTier>;
  team_tier?: Maybe<SubscriptionTier>;
  effective_tier?: Maybe<SubscriptionTier>;
  ehr_provider?: Maybe<EHRProvider>;
  has_integration?: Maybe<boolean>;
  integration_onboarding_provider?: Maybe<EHRProvider>;
  integration_onboarding_created_at?: Maybe<string>;
  integration_onboarding_updated_at?: Maybe<string>;
  source?: Maybe<string>;
  integration_requests?: Maybe<IntegrationRequest[]>;
  mfa_enabled?: boolean;
  mfa_trigger_source?: string;
  opted_in_experimental?: boolean;
  session_page_v2_enrollment_source?: SessionPageV2EnrollmentSource;
  desktop_version?: string;
  // `app_version` (added 2026-05-11 alongside APP-4897): cross-platform
  // app-version field. Today only desktop populates it; web/mobile should
  // backfill so dashboards can break down by `client_platform`+`app_version`
  // instead of `desktop_version` / `ios_version` / etc. Keep `desktop_version`
  // for backward compatibility with existing dashboards and PostHog flag
  // release conditions until they're migrated.
  app_version?: string;
  go_live_time_stamp?: Maybe<string>;
  onboarding_free_text?: Maybe<string>;
  other_specialty?: Maybe<string>;
  display_language?: Maybe<LanguageCode>;
  team_info: {
    team_name?: Maybe<string>;
    team_id?: Maybe<string>;
    user_team_plan?: Maybe<string>;
    user_joined_team_date?: Maybe<string>;
  };
  specialty?: Maybe<string>;
  localised_specialty?: Maybe<string>;
  specialty_group?: Maybe<string>;
  user_role_list?: Maybe<
    {
      id: string;
      name: string;
      config: RoleModel['config'];
    }[]
  >;
  from?: Maybe<string>;
  pathname?: Maybe<string>;
  evidence_access?: Maybe<string>;
  is_evidence_activated?: Maybe<boolean>;
  is_managed?: Maybe<boolean>;
}

export function identify(input: {
  authID: string;
  userTraits: {
    email: string;
    email_domain?: string;
    user_ref_id?: Maybe<string>;
    App: AppType;
    country: Maybe<string>;
    region: Maybe<Region>;
    given_name: Maybe<string>;
    family_name: Maybe<string>;
    phone_number?: Maybe<string>;
    has_phone_number?: boolean;
    user_team_id: Maybe<string>;
    user_team_name: Maybe<string>;
    user_team_role: Maybe<UserRole>;
    user_team_plan: Maybe<string>;
    user_team_mfa_enabled: Maybe<boolean>;
    user_joined_team_date: Maybe<string>;
    user_created_date?: Maybe<string>;
    user_tier: Maybe<SubscriptionTier>;
    team_tier: Maybe<SubscriptionTier>;
    effective_tier: Maybe<SubscriptionTier>;
    onboarding_selected_ehr_provider: Maybe<string>;
    ehr_provider: Maybe<EHRProvider>;
    has_integration: Maybe<boolean>;
    // Flag-targeting cohort properties, all read from the same
    // IntegrationOnboardingSession document. Distinct from `ehr_provider`
    // above, which can also resolve from a generic launch or the legacy
    // EHR profile.
    integration_onboarding_provider?: Maybe<EHRProvider>;
    integration_onboarding_created_at?: Maybe<string>;
    integration_onboarding_updated_at?: Maybe<string>;
    /** Active workspace for PostHog cohort targeting. */
    workspace_id?: Maybe<string>;
    source: Maybe<string>;
    signup_attributed_product?: Maybe<string>;
    integration_requests?: Maybe<IntegrationRequest[]>;
    mfa_enabled?: boolean;
    mfa_trigger_source?: string;
    opted_in_experimental?: boolean;
    session_page_v2_enrollment_source?: SessionPageV2EnrollmentSource;
    go_live_time_stamp: Maybe<string>;
    onboarding_free_text: Maybe<string>;
    user_role_list?: Maybe<RoleModel[]>;
    specialty: Maybe<ClinicianTypeInput>;
    localised_specialty: Maybe<ClinicianTypeInput>;
    other_specialty: Maybe<string>;
    display_language: Maybe<LanguageCode>;
    from?: Maybe<string>;
    pathname?: Maybe<string>;
    cpuArch?: string;
    seen_modals?: Maybe<SeenModals>;
    evidence_billing_info?: Maybe<{
      tier: Maybe<string>;
      source: Maybe<string>;
      is_in_free_trial: boolean;
      has_trial_expired: boolean;
      is_stripe_managed: boolean;
    }>;
    evidence_access?: Maybe<string>;
    is_evidence_activated?: Maybe<boolean>;
    is_managed?: Maybe<boolean>;
    /** Enabled access entitlements for PostHog cohort targeting. */
    features?: string[];
  };
}) {
  const { authID, userTraits } = input;
  const { features, ...traitsWithoutPosthogOnlyFields } = userTraits;
  const commonTraits: CommonTraits = {
    ...traitsWithoutPosthogOnlyFields,
    // `App` (legacy Web/Desktop identity trait) is caller-provided and kept for
    // existing PostHog/Segment/Flagsmith targeting. The finer device/build
    // dimensions (app_type / client_platform / platform / installer_type /
    // distribution_channel / mdm_provider) are NOT user identity — they ride
    // per-event via trackEventV2 rather than as last-write-wins user properties
    // (bce3749bf0 set them on identify; reverted — device dims aren't identity).
    App: getAppType(),
    // Restores APP-4897 per-user filtering on PostHog / Amplitude / Segment
    // / Datadog RUM that broke when APP-4507 (#9756) dropped `desktop_version`
    // from the identify payload. Emits `app_version` in parallel —
    // see `getAppVersionTraits` and `CommonTraits.app_version` for context.
    ...getAppVersionTraits(),
    team_info: {
      team_name: userTraits.user_team_name,
      team_id: userTraits.user_team_id,
      user_team_plan: userTraits.user_team_plan,
      user_joined_team_date: userTraits.user_joined_team_date,
    },
    specialty: userTraits.specialty?.name,
    localised_specialty: userTraits.localised_specialty?.name,
    specialty_group: userTraits.specialty?.group?.name,
    user_role_list: userTraits.user_role_list?.map((role) => ({
      id: role.id,
      name: role.name,
      config: role.config,
    })),
    from: userTraits.from,
    pathname: userTraits.pathname,
  };

  // Segment-specific traits. Ad-platform attribution ids are Segment-only:
  // the Meta / Google Ads CAPI insert functions read them from the unified
  // profile (Meta fbc/fbp; Google gclid/gbraid/wbraid) for later
  // server-side conversions.
  const segmentTraits = {
    ...commonTraits,
    ...getAttributionIdentifyTraits(),
    $groups: userTraits.user_team_id
      ? { segment_group: userTraits.user_team_id }
      : undefined,
  };

  datadogRum.setUser({
    id: authID,
    ...commonTraits,
  });
  setSnowplowUserId(authID);
  setSnowplowUserContext({
    session_page_v2_enrollment_source:
      commonTraits.session_page_v2_enrollment_source ?? null,
  });
  // NOTE: the Snowplow client_context `update_channel` is NOT set here.
  // identify can run before the Kinde-properties query resolves, which would
  // strand Snowplow at null for the session. It's set from
  // OpenFeatureContextSync instead — that hook subscribes to
  // useQueryKindeProperties, so it re-attaches the channel once the region
  // resolves (APP-6116).
  // `posthog.identify` already persists these traits and emits the events
  // PostHog expects: `$identify` on first identification, then `$set`
  // whenever a trait actually changes (it hashes distinct_id + $set and
  // no-ops when nothing differs). A manual `capture('$identify')` used to
  // follow this line to feed two Braze feature-flag destinations, but it
  // fired unconditionally — ~4.25M events a week, 85% of the `$identify`
  // volume — to deliver attributes those destinations then deduplicated to
  // one delivery per person per hour. Both destinations have since been
  // deleted, so the duplicate is pure cost. Note that Braze profile
  // identity comes from Segment (below), not PostHog, and Braze
  // transactional email is sent by the backend.
  // `pathname` is deliberately withheld from PostHog, and ONLY from PostHog.
  // PostHog hashes (distinct_id + $set + $set_once) and short-circuits an
  // identify whose hash is unchanged, so a trait that changes on every
  // navigation forces a billable `/flags` reload plus a `$set` event per
  // route change. Amplitude / Segment / Datadog RUM have no such cost and
  // still receive it, so this filter must not move up into `commonTraits`.
  const posthogOmittedTraits = new Set(['pathname']);
  const posthogTraits = Object.fromEntries(
    Object.entries(commonTraits).filter(([key]) => !posthogOmittedTraits.has(key))
  );
  if (features) {
    posthogTraits.features = features;
  }
  posthog.identify(authID, posthogTraits);
  void segmentClient.identify(authID, segmentTraits);

  // Prefer user_ref_id as the canonical Amplitude user id (cross-platform,
  // stable across auth providers). Falls back to the Kinde id only on the
  // first identify before useQueryUser has resolved. Note: this creates a
  // brief window where events are attributed to a Kinde-id-keyed profile
  // before the second identify swaps to user_ref_id — Amplitude's SDK
  // auto-merges anonymous-device activity onto a named user, but it does
  // NOT auto-merge two named user ids. The two profiles are explicitly
  // stitched by the one-off /usermap backfill in ml-scribe PR #9469.
  amplitude.setUserId(userTraits.user_ref_id ?? authID);

  // if sending email field, get the domain of the email to set email_domain property
  if (commonTraits.email) {
    const emailDomain = commonTraits.email.split('@')[1];
    commonTraits.email_domain = emailDomain;
  }

  commonTraits.has_phone_number = Boolean(commonTraits.phone_number);

  // Filter out PII fields before sending to Amplitude
  const piiFields = new Set(['email', 'given_name', 'family_name', 'phone_number']);

  const amplitudeTraits = Object.fromEntries(
    Object.entries(commonTraits).filter(([key]) => !piiFields.has(key))
  );

  const identifyObj = new AmplitudeIdentify();
  for (const [key, value] of Object.entries(amplitudeTraits)) {
    if (value != null) {
      identifyObj.set(key, value as AmplitudePropertyType);
    }
  }

  // Retain kinde_user_id as a user property so historical dashboards and
  // cohorts that filtered on it continue to resolve after the user-id swap.
  identifyObj.set('kinde_user_id', authID);

  const currentAuthProvider = readStoreAuthProviderForAnalytics();
  const providerValue = mapAuthProviderToProvider(currentAuthProvider);
  if (providerValue) {
    identifyObj.set('authentication_provider', providerValue);
  }

  amplitude.identify(identifyObj);

  // Set group for team-based analytics
  if (userTraits.user_team_id) {
    amplitude.setGroup('team', userTraits.user_team_id);
  }

  // RC custom signals for Desktop targeting — shared Mobile+Desktop keys
  // (hardware signals set from chronicle sync). Firebase SDK loads lazily
  // inside the custom-signals helper, not at import time.
  // identify is shared with web/FHIR; only Desktop may claim DesktopApp —
  // null clears a stale platform signal if this bag was written earlier.
  setRemoteConfigCustomSignals({
    ...(authID ? { kinde_user_id: authID } : {}),
    native_platform_source: isTauri() ? DESKTOP_NATIVE_PLATFORM_SOURCE : null,
    os_version: formatRemoteConfigOsVersion(getDesktopOsVersion()),
    region: formatRemoteConfigRegion(
      userTraits.region != null ? String(userTraits.region) : null
    ),
    ...(userTraits.user_ref_id ? { user_ref_id: userTraits.user_ref_id } : {}),
  });
}
